"use client";

import type { CSSProperties } from "react";
import Image from "next/image";
import styles from "./brand-slider.module.css";

export interface PartnerLogo {
  id: string;
  title: string;
  logo: string;
  isExternalLink: boolean;
  externalLink?: string;
}

// Keep the marquee comfortably wider than any viewport even when only a few
// logos exist, so the seamless -50% loop never reveals empty space.
const MIN_ITEMS_PER_GROUP = 8;

export default function BrandSliderClient({
  logos,
}: {
  logos: PartnerLogo[];
}) {
  if (!logos.length) return null;

  // Repeat the logos until a single group is wide enough to fill the screen.
  const repeats = Math.max(1, Math.ceil(MIN_ITEMS_PER_GROUP / logos.length));
  const group: PartnerLogo[] = Array.from({ length: repeats }, () => logos).flat();

  // Pace the scroll by the amount of content so speed feels consistent.
  const duration = Math.max(25, group.length * 4);

  const renderGroup = (ariaHidden: boolean) => (
    <div className={styles.group} aria-hidden={ariaHidden}>
      {group.map((logo, i) => {
        const img = (
          <Image
            src={logo.logo}
            alt={logo.title || "Partner Logo"}
            width={217}
            height={78}
            unoptimized
          />
        );

        return (
          <div className={styles.box} key={`${logo.id}-${i}`}>
            {logo.isExternalLink && logo.externalLink ? (
              <a
                className={styles.link}
                href={logo.externalLink}
                target="_blank"
                rel="noreferrer"
                aria-label={logo.title || "Partner"}
              >
                {img}
              </a>
            ) : (
              img
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={`brand-area space-top ${styles.brandArea}`}>
      <div
        className={styles.marquee}
        style={{ "--marquee-duration": `${duration}s` } as CSSProperties}
      >
        <div className={styles.track}>
          {renderGroup(false)}
          {renderGroup(true)}
        </div>
      </div>
    </div>
  );
}
